
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Subscription } from "@/components/subscriptions/types";
import { API_ENDPOINT, getAuthHeader } from "@/config/environment";

export function useSchoolSubscriptions() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSubscriptionsForSchool = async (schoolId: string, token?: string | null) => {
    try {
      setLoading(true);
      console.log('Fetching subscriptions for school ID:', schoolId);
      
      // Construct URL with token if available
      let url = `${API_ENDPOINT}/get-school-subscriptions?schoolId=${encodeURIComponent(schoolId)}`;
      if (token) {
        url += `&token=${encodeURIComponent(token)}`;
      }
      
      // Use GET request instead of POST
      const response = await fetch(
        url,
        {
          method: 'GET',
          headers: getAuthHeader()
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to fetch subscriptions';
        try {
          const errorData = await response.json();
          console.error('Error response from API:', errorData);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      
      if (!responseData.success) {
        console.error('Unsuccessful response:', responseData);
        throw new Error(responseData.error || 'Failed to fetch subscriptions');
      }

      console.log('Fetched subscriptions data:', responseData.data);
      
      const { subscriptions: subscriptionsData, infradigital } = responseData.data;
      
      if (subscriptionsData) {
        setSubscriptions(subscriptionsData);
        return infradigital;
      } else {
        setSubscriptions([]);
        return null;
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to fetch subscriptions",
      });
      setSubscriptions([]);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    subscriptions,
    loading,
    fetchSubscriptionsForSchool
  };
}
